import { CandidateJobCompetitiveIntelligence, CandidateJobInsights, Job } from "@type/job";
import { Button } from "@component/button";
import { ArrowLeftIcon, Award, StarIcon, UsersIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { formatHumanReadable } from "@util/date";
import { Link, useNavigate } from "react-router-dom";
import { getCurrentUser } from "@service/user";
import { interviewStatusPendingReview, mapJobRateFrequency } from "@constant/app";
import { convertCurrency } from "@util/currency";
import { generateUserReferral, getUserReferral, getUserReferralByCode } from "@service/referral";
import { ReferralUserInfo } from "@type/referral";
import { useQuery } from "@util/useQuery";
import { catchErrorLog } from "@util/sentry";
import { useUserProfileContext } from "@context/user_profile";
import { getCompetitiveIntelligence, getJobInsights } from "@service/job";
import DialogShareReferral from "@container/dialog/dialog-share-referral";
import ReferralNote from "@container/referral-note";
import ApplyForm from "@container/apply-form";
import DialogApplyNow from "@container/dialog/dialog-apply-now";
import CandidateInsights from "@container/candidate-job-insight";
import { trackEvent } from "@util/mixpanel";

interface Props {
    job: Job
    isGuestMode: boolean
}

export default function JobDetail(props: Props) {
    const navigate = useNavigate();
    const queryParams = useQuery();
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const [isOpenDialogReferral, setOpenDialogReferral] = useState<boolean>(false);
    const [isOpenDialogApplyNow, setOpenDialogApplyNow] = useState<boolean>(false);
    const [referralUserInfo, setReferralUserInfo] = useState<ReferralUserInfo | undefined>(undefined);
    const [isHaveReferralLink, setIsHaveReferralLink] = useState<boolean>(false);
    const [isGeneratingReferralLink, setIsGeneratingReferralLink] = useState<boolean>(false);
    const [referralCode, setReferralCode] = useState<string>("");
    const [candidateInsights, setCandidateInsights] = useState<CandidateJobInsights | undefined>(undefined);
    const [candidateJobCompetitiveIntelligence, setCandidateJobCompetitiveIntelligence] = useState<CandidateJobCompetitiveIntelligence | undefined>(undefined);
    const [isLoadingCandidateInsights, setIsLoadingCandidateInsights] = useState<boolean>(true);
    const [isJobApplied, setIsJobApplied] = useState<boolean>(false);

    useEffect(() => {
        if (queryParams.get("referral")) {
            getUserReferralByCode(queryParams.get("referral") || "").then((response) => {
                if (response && response.data.code === 200) {
                    setReferralUserInfo(response.data.data);
                    sessionStorage.setItem("__referral_code__", response.data.data.referral_code || "");
                }
            })
        } else {
            sessionStorage.removeItem("__referral_code__");
        }
    }, [queryParams.get("referral")]);

    const loadReferralUserInfo = () => {
        if (!props.isGuestMode && props.job.referral_bonus > 0) {
            getUserReferral().then((response) => {
                if (response && response.data.code === 200) {
                    setIsHaveReferralLink(!!response.data.data.referral_code);
                    setReferralCode(response.data.data.referral_code || "");
                }
            }).catch((error) => {
                catchErrorLog(`Error fetching referral link: ${error}`);
            });
        }
    }

    const loadCurrentUser = (onGetCV: (user_id: string, email: string, name: string) => void) => {
        getCurrentUser().then((response) => {
            if (!response) return;

            let name = response.data?.data?.first_name || "";
            if (response.data?.data?.last_name) {
                name += " " + response.data?.data?.last_name || "";
            }

            onGetCV(response.data?.data?.id || "", response.data?.data?.email || "", name)
        })
    }

    useEffect(() => {
        loadReferralUserInfo();
    }, [props.isGuestMode])

    useEffect(() => {
        if (userInfo && userInfo.role === "job_seeker") {
            getJobInsights(props.job.id).then((resp) => {
                if (resp && resp.data.code === 200) {
                    setCandidateInsights(resp.data.data);
                }
            }).catch((error) => {
                catchErrorLog(`Error fetching job insights: ${error}`);
            }).finally(() => {
                setIsLoadingCandidateInsights(false);
            });
        }
    }, [userInfo]);

    useEffect(() => {
        if (userInfo && props.job.id) {
            getCompetitiveIntelligence(props.job.id, userInfo.id).then((response) => {
                if (response.status === 200) {
                    setCandidateJobCompetitiveIntelligence(response.data.data);
                }
            }).catch((error) => {
                catchErrorLog(`Error fetching competitive intelligence: ${error}`);
            });
        }
    }, [userInfo, props.job.id]);

    const handleShowDialogSocial = () => {
        trackEvent("job_referral_clicked");
        if (isHaveReferralLink) {
            setOpenDialogReferral(true);
            return;
        }

        if (props.isGuestMode) {
            navigate("/", { state: { from: window.location.pathname } });
            return;
        }

        setIsGeneratingReferralLink(true);
        generateUserReferral().then((response) => {
            if (response && response.data.code === 200) {
                setIsHaveReferralLink(true);
                setReferralCode(response.data.data.referral_code || "");
                setOpenDialogReferral(true);

                if (queryParams.get("referral")) {
                    navigate(`/job/${props.job.id}`, { replace: true });
                    setReferralUserInfo(undefined);
                }
            }
        }).catch((error) => {
            if (error.response && error.response.data.code === 400) {
                loadReferralUserInfo();
            }
            catchErrorLog(`Error generating referral link: ${error}`);
        }).catch((error) => {
            catchErrorLog(`Error get referral link: ${error}`);
        }).finally(() => {
            setIsGeneratingReferralLink(false);
        })
    }

    return (
        <>
            <div className={`animate-slide-up container mx-auto mt-8 ${props.isGuestMode ? "py-8" : "py-0"} px-4 pb-8`}>
                <div className="grid md:grid-cols-[1fr,400px] gap-8">
                    <div className="flex-1">
                        <div className="flex flex-col">
                            <div className="space-y-1 sm:space-y-4">
                                <h1 className="text-3xl font-medium">{props.job.job_title}</h1>
                                <div className="flex items-center text-gray-600">
                                    {props.job.department && (
                                        <>
                                            <span>{props.job.department}</span>
                                        </>
                                    )}
                                    {props.job.location && (
                                        <>
                                            <span className="mx-2">•</span>
                                            {props.job?.location_metadata?.googleMapsUri ? (
                                                <a target="_blank" className="hover:underline" href={props.job?.location_metadata?.googleMapsUri}>{props.job.location}</a>
                                            ) : (
                                                <span>{props.job.location}</span>
                                            )}
                                        </>
                                    )}
                                </div>

                                <div className="flex items-center gap-3 pt-4">
                                    {props.job.is_special_job ? (
                                        <div
                                            className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-md flex items-center justify-center"
                                        >
                                            <StarIcon className="text-white" />
                                        </div>
                                    ) : (
                                            <div className="relative flex-shrink-0 w-12 h-12 rounded-full overflow-hidden flex justify-center items-center bg-white ring-1 ring-blue-200 dark:ring-offset-gray-900">
                                                <img
                                                    src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${props.job.company_logo}`}
                                                    alt={`${props.job.company_name} logo`}
                                                    onError={(e) => {
                                                        e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/company_placeholder.png`;
                                                    }}
                                                    className="relative w-12 object-cover "
                                                />
                                            </div>
                                    )}
                                    <div className="flex flex-col">
                                        <div className="text-sm">
                                            {props.job?.main_company_name ? props.job?.company_name !== props.job?.main_company_name ? `${props.job?.company_name} by ${props.job?.main_company_name}` : props.job?.main_company_name : props.job?.company_name}
                                        </div>
                                        <div className="text-sm text-muted-foreground">{props.job.company_is_byoc ? props.job.company_website : "ourteam.ai"}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8">
                                <div className="space-y-6">
                                    <section>
                                        <h2 className="text-lg font-semibold mb-2 text-black">Description</h2>
                                        <div className="text-black content" dangerouslySetInnerHTML={{ __html: props.job.job_description || "" }} />
                                    </section>

                                    <section>
                                        {props.job.is_ourteam && (
                                            <>
                                                <h2 className="text-lg font-semibold mb-2 text-black">About {props.job.company_name}</h2>
                                                <div className="text-black">
                                                    {props.job.company_description}
                                                </div>
                                            </>
                                        )}
                                    </section>

                                    {((userInfo?.role === "job_seeker" || props.isGuestMode) && props.job.referral_bonus > 0) && (
                                        <ReferralNote
                                            referralCode={referralCode}
                                            referralAmount={props.job.referral_bonus}
                                            isHaveReferralLink={isHaveReferralLink}
                                            currency={props.job.referral_currency || ""}
                                            jobLink={"/job/" + props.job.id}
                                            jobTitle={props.job.job_title}
                                            companySubdomain={props.job.company_subdomain || ""}
                                            onCompleted={() => {
                                                setReferralUserInfo(undefined);
                                            }}
                                        />
                                    )}
                                    {userInfo && userInfo.role === "job_seeker" && (
                                        <CandidateInsights
                                            isLoading={isLoadingCandidateInsights}
                                            matches={candidateInsights?.matches || []}
                                            lackingAreas={candidateInsights?.lacking_areas || []}
                                            improvements={candidateInsights?.improvements || []}
                                        />
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="hidden sm:block">
                        <div className="sticky top-6">
                            {userInfo && userInfo.role === "job_seeker" && candidateJobCompetitiveIntelligence && candidateJobCompetitiveIntelligence.top_n <= 10 && (
                                <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 mb-4">
                                    <div className="flex items-start">
                                        <div className="flex items-center gap-2">
                                            <Award className="w-5 h-5 text-blue-700 flex-shrink-0" />

                                            <span className="text-sm text-gray-700" style={{ color: "#1d4ed8" }}>
                                                You're in the top 10% of all applicants for this role.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="border rounded-xl p-6 space-y-6 shadow-md">
                                <div className="space-y-1">
                                    <h2 className="text-xl font-bold">
                                        {((props.job.min_rate || 0) === 0 || (props.job.max_rate || 0) === 0) && props.job.fixed_rate === 0 ? "Competitive compensation (DOE)" : (
                                            <>
                                                {(props.job.min_rate && props.job.max_rate) ? `${convertCurrency(props.job.min_rate, props.job.rate_currency || "USD")}-${convertCurrency(props.job.max_rate, props.job.rate_currency || "USD")}` : convertCurrency(props.job.fixed_rate, props.job.rate_currency || "USD")}
                                                {props.job.pay_rate_frequency !== "One Time" ? ` per ${mapJobRateFrequency[props.job.pay_rate_frequency as keyof typeof mapJobRateFrequency] || ""}` : ""}
                                            </>
                                        )}
                                    </h2>
                                    <p className="text-black">{props.job.job_type}{props.job.work_arrangement ? ` (${props.job.work_arrangement})` : ""}</p>
                                </div>

                                {!isLoadingUserProfile && (
                                    <div className="space-y-4">
                                        {!!props.job && (
                                            <ApplyForm
                                                job={props.job}
                                                isQuickApply={props.job.company_is_quick_apply || false}
                                                isGuestMode={props.isGuestMode}
                                                onReloadCurrentUser={loadCurrentUser}
                                                onJobApplied={(isApplied: boolean) => {
                                                    setIsJobApplied(isApplied);
                                                }}
                                            />
                                        )}

                                        {(userInfo?.role === "job_seeker" && props.job.referral_bonus > 0) && (
                                            <Button
                                                variant="outline"
                                                className="w-full"
                                                disabled={isGeneratingReferralLink}
                                                onClick={handleShowDialogSocial}
                                            >
                                                Refer & earn {convertCurrency(props.job.referral_bonus || 0, props.job.referral_currency || "")}
                                            </Button>
                                        )}
                                        {props.job.job_status === "Paused" && (
                                            <div className="text-sm text-center text-red-600 font-medium">
                                                This job is currently not accepting applications. Please check back later.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {(referralUserInfo && referralUserInfo.user_id !== userInfo?.id) && (
                                <div className="bg-green-100 text-green-700 p-3 rounded-xl flex items-center mt-4">
                                    <UsersIcon className="h-4 w-4 mr-2" />
                                    <span className="text-sm">You've been referred by {[referralUserInfo.first_name, referralUserInfo.last_name].join(" ")}.</span>
                                </div>
                            )}
                            <div className="text-sm text-right text-muted-foreground mt-4">
                                {props.job.updated_at && new Date(props.job.updated_at) > new Date(props.job.created_at) ? `Updated ${formatHumanReadable(props.job.updated_at, true)}` : `Posted ${formatHumanReadable(props.job.created_at, true)}`}<br />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {props.job.referral_bonus > 0 && (
                <DialogShareReferral
                    open={isOpenDialogReferral}
                    jobTitle={props.job.job_title}
                    jobLink={"/job/" + props.job.id}
                    companySubdomain={props.job.company_subdomain || ""}
                    onClose={() => setOpenDialogReferral(false)}
                />
            )}

            <DialogApplyNow
                open={isOpenDialogApplyNow}
                onClose={() => setOpenDialogApplyNow(false)}
                job={props.job}
                onReloadCurrentUser={loadCurrentUser}
                onJobApplied={(isApplied: boolean) => {
                    setIsJobApplied(isApplied);
                }}
                isJobApplied={isJobApplied}
            />

            <footer className="md:hidden fixed bottom-0 bg-white border-t w-full h-[75px] flex flex-row items-center justify-between px-4">
                <div>
                    <div>
                        <h3 className="text-lg line-clamp-1 font-medium">{((props.job.min_rate || 0) === 0 || (props.job.max_rate || 0) === 0) && props.job.fixed_rate === 0 ? "Competitive compensation (DOE)" : (
                            <>
                                {(props.job.min_rate && props.job.max_rate) ? `${convertCurrency(props.job.min_rate, props.job.rate_currency || "USD")}-${convertCurrency(props.job.max_rate, props.job.rate_currency || "USD")}` : convertCurrency(props.job.fixed_rate, props.job.rate_currency || "USD")}
                                {` per ${mapJobRateFrequency[props.job.pay_rate_frequency as keyof typeof mapJobRateFrequency] || ""}`}
                            </>
                        )}</h3>
                        <p className="text-gray-500 text-[15px] mt-[-0.5px]">{props.job.job_type}{props.job.work_arrangement ? ` (${props.job.work_arrangement})` : ""}</p>
                    </div>
                </div>
                <Button variant="default" size="lg" onClick={() => setOpenDialogApplyNow(true)}>Apply Now</Button>
            </footer>
        </>
    )
}